import { addMonths, differenceInDays, format, startOfMonth } from 'date-fns/fp';
import { useState } from 'react';
import { caloTheme } from 'src/theme/calo';

import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';

import { CircularProgressContainer } from './CircularProgressContainer';
import { NextPaymentProgress } from './NextPaymentProgress';

const PaymentProgress = () => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const nextPaymentDate = format('MMM d')(startOfMonth(addMonths(1)(new Date())));
  const nextPaymentDateFull = format('d MMM yyyy')(startOfMonth(addMonths(1)(new Date())));

  const calculatePaymentProgress = () => {
    const now = new Date();
    const currentMonthStart = startOfMonth(now);
    const nextMonthStart = startOfMonth(addMonths(1)(now));

    const totalDaysInCycle = differenceInDays(nextMonthStart)(currentMonthStart);
    const daysElapsed = differenceInDays(now)(currentMonthStart);

    const progressPercentage = Math.min(Math.max((daysElapsed / totalDaysInCycle) * 100, 0), 100);

    return Math.round(progressPercentage);
  };

  const paymentProgress = calculatePaymentProgress();
  const isComplete = paymentProgress >= 100;

  return (
    <Tooltip
      title={`Next payout: ${nextPaymentDateFull}`}
      arrow
      placement="top"
      open={tooltipOpen}
      onClose={() => setTooltipOpen(false)}
      enterDelay={200}
      leaveDelay={0}
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: caloTheme.palette.neutral800,
            color: caloTheme.palette.white,
            padding: '8px 16px',
            fontSize: '14px',
            borderRadius: '8px',
            zIndex: 9999
          }
        },
        arrow: {
          sx: {
            color: caloTheme.palette.neutral800
          }
        }
      }}
    >
      <div
        onMouseEnter={() => setTooltipOpen(true)}
        onMouseLeave={() => setTooltipOpen(false)}
        style={{ display: 'inline-block' }}
      >
        <NextPaymentProgress>
          <CircularProgressContainer>
            <CircularProgress
              variant="determinate"
              value={100}
              size={150}
              thickness={3}
              sx={{
                color: isComplete ? caloTheme.palette.secondaryTeal500 : caloTheme.palette.primary500,
                opacity: 0.3,
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round'
                }
              }}
            />
            <CircularProgress
              variant="determinate"
              value={paymentProgress}
              size={150}
              thickness={3}
              sx={{
                color: isComplete ? caloTheme.palette.secondaryTeal500 : caloTheme.palette.primary500,
                position: 'absolute',
                left: 0,
                '& .MuiCircularProgress-circle': {
                  strokeLinecap: 'round'
                }
              }}
            />
            <Typography
              variant="h6"
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                color: '#111827',
                fontSize: { xs: '24px', sm: '30px' },
                lineHeight: 1
              }}
            >
              {nextPaymentDate}
            </Typography>
          </CircularProgressContainer>
          <Box sx={{ textAlign: 'center', color: '#6B7280', mt: 1 }}>
            <Typography variant="caption" display="block" sx={{ fontSize: { xs: '16px', sm: '18px' } }}>
              Next Payment
            </Typography>
          </Box>
        </NextPaymentProgress>
      </div>
    </Tooltip>
  );
};

export default PaymentProgress; 